import { useEffect, useState } from "react";
import { Upload as UploadIcon, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter 35: typed image portfolio uploader. Every photo carries a mandatory
 * `photo_type` tag (Headshot / Full Body / Traditional / etc.) so casting
 * directors can filter later. Rendered as a stand-alone widget so any page
 * (Upload, Edit Profile, Settings) can drop it in without duplicating logic.
 */
export default function GalleryUploader({ initialGallery = [] }) {
  const [gallery, setGallery] = useState(initialGallery);
  const [types, setTypes] = useState([]);
  const [pendingType, setPendingType] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/gallery/photo-types").then(r => setTypes(r.data.types || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!pendingFile) return toast.error("Pick an image");
    if (!pendingType) return toast.error("Select a photo type — Casting directors filter by this");
    if (gallery.length >= 10) return toast.error("Max 10 photos");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("photo_type", pendingType);
      const { data } = await api.post("/users/me/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGallery(data.gallery_photos || []);
      setPendingFile(null);
      toast.success("Photo added to your portfolio");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (imgId) => {
    if (!window.confirm("Remove this photo from your portfolio?")) return;
    try {
      const { data } = await api.delete(`/users/me/gallery/${imgId}`);
      setGallery(data.gallery_photos || []);
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const typeLabel = (k) => (types.find(t => t.key === k) || { label: k }).label;

  return (
    <div data-testid="gallery-uploader" className="space-y-3">
      <div className="rounded-2xl border border-[#F5C462]/30 bg-gradient-to-br from-[#F5C462]/5 to-transparent p-3 space-y-2">
        <p className="text-xs text-zinc-300 leading-relaxed">
          <span className="text-[#F5C462] font-semibold">Portfolio photos ({gallery.length}/10):</span> Every image needs a photo type so casting directors can filter — Headshot, Traditional, Character Look, etc.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="sm:col-span-2">
            <label className="rounded-xl border border-dashed border-zinc-700 bg-zinc-900/60 p-3 h-11 flex items-center gap-2 cursor-pointer hover:border-[#F5C462]/50 transition-colors text-xs text-zinc-300">
              <ImageIcon size={14} className="text-[#F5C462]"/>
              <span className="truncate">{pendingFile ? pendingFile.name : "Choose image (JPG/PNG/WebP · up to 8MB)"}</span>
              <input data-testid="gallery-file" type="file" accept="image/*" className="hidden" onChange={(e) => setPendingFile(e.target.files?.[0] || null)}/>
            </label>
          </div>
          <Select value={pendingType} onValueChange={setPendingType}>
            <SelectTrigger data-testid="gallery-type" className="bg-zinc-900 border-zinc-800 h-11"><SelectValue placeholder="Photo type *"/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="gallery-upload-btn" disabled={busy || !pendingFile || !pendingType || gallery.length >= 10} onClick={submit} className="w-full h-10 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-sm font-semibold disabled:opacity-40">
          {busy ? <><Loader2 className="animate-spin mr-1" size={14}/> Uploading…</> : <><UploadIcon size={14} className="mr-1"/> Add to portfolio</>}
        </Button>
      </div>

      {gallery.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {gallery.map((g) => (
            <div key={g.id} data-testid={`gallery-item-${g.id}`} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-zinc-800 bg-black group">
              <img src={`${BACKEND}/api/users/photo/${g.path}`} alt="" className="w-full h-full object-cover"/>
              <span className="absolute top-2 left-2 text-[10px] rounded-full bg-black/70 text-[#F5C462] border border-[#F5C462]/40 px-2 py-0.5 font-semibold backdrop-blur">
                {typeLabel(g.photo_type)}
              </span>
              <button data-testid={`gallery-remove-${g.id}`} onClick={() => remove(g.id)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 border border-zinc-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/80">
                <X size={12}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
